import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { IMAGES } from "@/data/content";
import { Reveal, Eyebrow, SectionTitle } from "@/components/common";

const PILLARS = [
  { h: "History & Archaeology", p: "From the Indus Valley's planned cities to the empires of the Mauryas and Guptas — the material record of a civilisation.", img: IMAGES.sunWheel, to: "/timelines" },
  { h: "Philosophy & Thought", p: "Six classical darshanas, Buddhist and Jain logic, and centuries of rigorous debate about reality, mind, and knowledge.", img: IMAGES.templeCarved, to: "/philosophy" },
  { h: "Science & Mathematics", p: "Decimal place value, astronomy at Ujjain, the Kerala school's infinite series, and the sciences of medicine and grammar.", img: IMAGES.templeTower, to: "/library/indic-civilisation" },
  { h: "Literature & Culture", p: "Sanskrit epics, Tamil Sangam poetry, bhakti song, and a visual culture of temple, bronze, and manuscript.", img: IMAGES.goddess, to: "/library" },
  { h: "Traditions & Religion", p: "Buddhism, Shaktism, Shaivism, Sikhism and more — studied as living traditions, not as settled dogma.", img: IMAGES.buddhaWheel, to: "/traditions" },
  { h: "Geography & Exchange", p: "Trade routes, pilgrimage networks, and the transmission of texts that carried Indic ideas across Asia.", img: IMAGES.himalaya, to: "/atlas" },
];

export default function Civilisation() {
  return (
    <div className="pt-16 lg:pt-[72px]" data-testid="civilisation-page">
      <section className="relative py-24 lg:py-32 border-b border-indic-gold/15">
        <img src={IMAGES.templeCarved} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1021]/70 via-[#0B1021]/85 to-indic-indigo" />
        <div className="relative mx-auto max-w-[1400px] px-4 sm:px-8">
          <Eyebrow>The Civilisation</Eyebrow>
          <h1 className="mt-4 font-serif text-5xl lg:text-7xl font-semibold text-amber-50 max-w-3xl leading-[1.05]">A plural field of knowledge, four thousand years deep</h1>
          <p className="mt-6 max-w-2xl text-lg text-slate-300/90 leading-relaxed">
            “Indic civilisation” names not a single race, state, or religion, but a long-lived and internally plural civilisational field centred on the subcontinent and extended across the world through trade, migration, pilgrimage, and the transmission of texts.
          </p>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8">
          <Reveal><SectionTitle eyebrow="Six Pillars" title="Ways into the civilisation" /></Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => (
              <Reveal key={p.h} delay={(i % 3) * 0.08}>
                <Link to={p.to} className="group block rounded-sm border border-indic-card-border bg-indic-card overflow-hidden hover:border-indic-gold/40 transition-colors h-full" data-testid={`pillar-${i}`}>
                  <div className="relative h-40 overflow-hidden">
                    <img src={p.img} alt={p.h} className="h-full w-full object-cover opacity-55 transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-indic-card to-transparent" />
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif text-2xl text-amber-50">{p.h}</h3>
                    <p className="mt-2 text-sm text-slate-300/85 leading-relaxed">{p.p}</p>
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-indic-gold">Explore <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
